/* Shared chip / cell components for Logo Enrichment surfaces.
   Extracted from LogoEnrichment.jsx (Iter 39 Phase 3) so the Run Detail
   page and the Launcher render identical visual semantics for every
   per-row outcome. NO LOGIC CHANGES vs. the originals — exact behavioural
   parity is intentional; if you need to tweak any chip, update both
   surfaces (which now read from this single module) in one place.

   PROP-002 / iter 85.12 — extended with Review Queue helpers:
     • ReviewStateChip  — Pending Review / Approved / Rejected / Skipped / Not Eligible
     • SourceMetaLine   — `Source: {logo_source} · {dim}  ·  Domain: {domain_source}`
     • ProvenanceBadges — lightweight quality chips on review cards
   These are additive — existing chips are unchanged. */

import { useState } from "react";
import { CheckCircle, XCircle, ImageBroken, ShieldCheck, Clock, Prohibit } from "@phosphor-icons/react";

export const SCORE_WEIGHTS = { tc: 0.20, bmr: 0.35, sp: 0.20, sa: 0.20, cp: 0.05 };
export const SCORE_LABELS = {
    tc: "token_coverage",
    bmr: "body_match_ratio",
    sp: "stem_specificity",
    sa: "source_authority",
    cp: "composite_penalty",
};

export function ConfidenceChip({ value }) {
    const map = {
        high: "bg-emerald-100 text-emerald-700",
        medium: "bg-blue-100 text-blue-700",
        low: "bg-amber-100 text-amber-700",
        "domain-only": "bg-slate-200 text-slate-700",
        none: "bg-slate-100 text-slate-500",
    };
    return (
        <span className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${map[value] || map.none}`}>
            {value || "—"}
        </span>
    );
}

export function ScoreCell({ score, breakdown, companyId }) {
    if (score === null || score === undefined) {
        return <span className="text-slate-300 text-xs">—</span>;
    }
    let tone;
    let gateLabel;
    if (score >= 0.55) { tone = "bg-emerald-100 text-emerald-700"; gateLabel = "write"; }
    else if (score >= 0.30) { tone = "bg-amber-100 text-amber-700"; gateLabel = "downgrade"; }
    else { tone = "bg-rose-100 text-rose-700"; gateLabel = "reject"; }
    return (
        <div className="relative group inline-block" data-testid={`score-cell-${companyId}`}>
            <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[11px] tabular-nums ${tone}`}
                title={`V2.0 gate: ${gateLabel}`}
            >
                {score.toFixed(3)}
            </span>
            {breakdown && Object.keys(breakdown).length > 0 && (
                <div
                    className="absolute z-30 top-full mt-1 right-0 hidden group-hover:block w-64 rounded-md border border-slate-200 bg-white shadow-lg p-2.5"
                    data-testid={`score-breakdown-${companyId}`}
                >
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">
                        V2.0 score breakdown
                    </div>
                    <table className="w-full text-[11px]">
                        <tbody>
                            {["tc", "bmr", "sp", "sa", "cp"].map((k) => {
                                const v = breakdown[k];
                                if (v === null || v === undefined) return null;
                                const w = SCORE_WEIGHTS[k];
                                return (
                                    <tr key={k} className="border-b border-slate-100 last:border-0">
                                        <td className="py-1 pr-2 text-slate-600">{SCORE_LABELS[k]}</td>
                                        <td className="py-1 px-1 text-slate-400 font-mono tabular-nums">×{w.toFixed(2)}</td>
                                        <td className="py-1 pl-1 text-right font-mono tabular-nums text-slate-900">{v.toFixed(2)}</td>
                                    </tr>
                                );
                            })}
                            <tr className="bg-slate-50">
                                <td className="py-1 pr-2 font-semibold text-slate-700">composite</td>
                                <td></td>
                                <td className="py-1 pl-1 text-right font-mono tabular-nums font-semibold text-slate-900">{score.toFixed(3)}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div className="text-[10px] text-slate-400 mt-1.5">
                        Thresholds: write ≥ 0.55 · downgrade ≥ 0.30 · reject &lt; 0.30
                    </div>
                </div>
            )}
        </div>
    );
}

export function OutcomeChip({ row }) {
    if (row.wrote) {
        return <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">wrote</span>;
    }
    if (row.would_write) {
        return <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">would write</span>;
    }
    if (row.suppress_commit || row.collision_with) {
        return <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">collision</span>;
    }
    if (row.third_party_category) {
        return (
            <span
                className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200"
                title={`Third-party host: ${row.third_party_host || "?"}`}
            >
                3rd-party
            </span>
        );
    }
    if (row.error) {
        return <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-100 text-rose-700" title={row.error}>error</span>;
    }
    if (row.skipped_reason) {
        return <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-200 text-slate-600" title={row.skipped_reason}>skipped</span>;
    }
    if (row.discovered_domain) {
        return <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">domain-only</span>;
    }
    return <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-400">unresolved</span>;
}

export function LogoCandidatePreview({ url }) {
    const [errored, setErrored] = useState(false);
    if (!url || errored) {
        return (
            <div
                data-testid="logo-candidate-empty"
                className="h-8 w-8 rounded bg-slate-100 border border-slate-200 flex items-center justify-center"
            >
                <ImageBroken size={12} className="text-slate-400" />
            </div>
        );
    }
    return (
        <img
            src={url}
            alt="logo candidate"
            data-testid="logo-candidate-preview"
            referrerPolicy="no-referrer"
            className="h-8 w-8 rounded object-contain bg-white border border-slate-200 p-0.5"
            onError={() => setErrored(true)}
        />
    );
}

export function WouldWriteCell({ row }) {
    const wouldWrite = !!row.would_write;
    const wouldWriteReason = row.skipped_reason
        || (row.collision_with ? `collision-with ${row.collision_with}` : "")
        || (!row.discovered_domain ? "domain-discovery-failed" : "")
        || (!row.candidate_logo_url ? "no-candidate-logo" : "");
    if (wouldWrite) {
        return (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700" data-testid={`would-write-yes-${row.company_id}`}>
                <CheckCircle size={12} weight="fill" />
                Yes
            </span>
        );
    }
    return (
        <span
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500"
            title={wouldWriteReason || "not eligible"}
            data-testid={`would-write-no-${row.company_id}`}
        >
            <XCircle size={12} weight="fill" className="text-slate-400" />
            No
            {wouldWriteReason && (
                <span className="ml-1 text-[10px] text-slate-400 font-mono truncate max-w-[140px]">
                    ({wouldWriteReason})
                </span>
            )}
        </span>
    );
}


// ─── PROP-002 / iter 85.12 — Review Queue helpers ───────────────────

/** Visual chip for the per-outcome review_state. */
export function ReviewStateChip({ state }) {
    const map = {
        pending_review: { label: "Pending Review", classes: "bg-amber-50 text-amber-800 border-amber-200", Icon: Clock },
        approved: { label: "Approved", classes: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: ShieldCheck },
        human_corrected: { label: "Human Corrected", classes: "bg-cyan-50 text-cyan-800 border-cyan-200", Icon: ShieldCheck },
        rejected: { label: "Rejected", classes: "bg-rose-50 text-rose-800 border-rose-200", Icon: XCircle },
        skipped: { label: "Skipped", classes: "bg-slate-100 text-slate-600 border-slate-200", Icon: Prohibit },
        not_eligible: { label: "Not Eligible", classes: "bg-slate-50 text-slate-500 border-slate-200", Icon: Prohibit },
    };
    const cfg = map[state] || map.not_eligible;
    const Icon = cfg.Icon;
    return (
        <span
            className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${cfg.classes}`}
            data-testid={`review-state-${state}`}
        >
            <Icon size={11} weight="fill" />
            {cfg.label}
        </span>
    );
}

/** One-line provenance for the review card.
 *  Format: `Source: {logo_source} · {dimensions}  ·  Domain: {domain_source}` */
export function SourceMetaLine({ outcome }) {
    const logoSource = outcome.logo_source || "—";
    const domainSource = outcome.domain_source || "—";
    const dims = Array.isArray(outcome.logo_dimensions) && outcome.logo_dimensions.length === 2
        ? `${outcome.logo_dimensions[0]}×${outcome.logo_dimensions[1]}`
        : null;
    return (
        <div className="text-xs text-slate-500" data-testid="source-meta-line">
            <span>Source: </span>
            <span className="font-mono text-slate-700">{logoSource}</span>
            {dims && (
                <>
                    <span className="text-slate-400"> · </span>
                    <span className="font-mono">{dims}</span>
                </>
            )}
            <span className="text-slate-300 mx-1.5">·</span>
            <span>Domain: </span>
            <span className="font-mono text-slate-700">{domainSource}</span>
        </div>
    );
}

/** Operator-amendment iter 85.12: lightweight provenance badges that
 *  surface candidate quality signals (Domain Verified, Logo Found,
 *  Heuristic Match, Collision Warning, …) without opening diagnostics.
 *  The backend (logo_review_engine.derive_provenance_badges) is the
 *  source of truth for the label/tone set; this component only paints. */
export function ProvenanceBadges({ badges }) {
    if (!Array.isArray(badges) || badges.length === 0) {
        return null;
    }
    const toneMap = {
        emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
        cyan: "bg-cyan-50 text-cyan-800 border-cyan-200",
        amber: "bg-amber-50 text-amber-800 border-amber-200",
        rose: "bg-rose-50 text-rose-800 border-rose-200",
        slate: "bg-slate-100 text-slate-700 border-slate-200",
    };
    return (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="provenance-badges">
            {badges.map((b, i) => (
                <span
                    key={`${b.label}-${i}`}
                    data-testid={`provenance-badge-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${toneMap[b.tone] || toneMap.slate}`}
                >
                    {b.label}
                </span>
            ))}
        </div>
    );
}
